/**
 * Sign-in error mapping to i18n KEYS (never localized copy, so callers own
 * t()), so no sign-in surface leaks a raw English Supabase string (rule #6).
 *
 * Email code sign-in uses the step-specific `mapEmailSendError` /
 * `mapEmailVerifyError`, which read the provider's error code and status.
 * `mapAuthError` (bottom) is the older message-only mapper, still used by the
 * Google buttons in AuthModal and AuthGateScreen; it is unchanged.
 */

/**
 * What useAuth's email calls return: the raw message plus the provider's
 * error details when there are any (auth-js AuthError `code` / `status`, and
 * `retryable` for AuthRetryableFetchError, where status 0 means the request
 * never got a response).
 */
export interface AuthCallResult {
  error: string | null;
  code?: string;
  status?: number;
  retryable?: boolean;
}

export type EmailErrorReason =
  | 'offline'
  | 'server'
  | 'rate-limited'
  | 'invalid-email'
  | 'invalid-code'
  | 'generic';

export interface MappedEmailError {
  /** i18n key under auth.* */
  key: string;
  reason: EmailErrorReason;
  /** Only when the provider stated a wait ("after N seconds") on a 429. */
  retryAfterSeconds: number | null;
}

const RATE_LIMIT_CODES = new Set(['over_email_send_rate_limit', 'over_request_rate_limit']);

function isConnectionFailure(result: AuthCallResult, online: boolean): boolean {
  return !online || (result.retryable === true && (result.status ?? 0) === 0);
}

function isServerFailure(result: AuthCallResult): boolean {
  return typeof result.status === 'number' && result.status >= 500;
}

function isRateLimited(result: AuthCallResult): boolean {
  return result.status === 429 || (result.code !== undefined && RATE_LIMIT_CODES.has(result.code));
}

/** Seconds stated by the provider, e.g. "…only request this after 42 seconds." */
export function parseRetryAfterSeconds(result: AuthCallResult): number | null {
  if (!isRateLimited(result)) return null;
  const match = /\bafter (\d{1,4}) seconds?\b/i.exec(result.error ?? '');
  if (!match) return null;
  const seconds = Number(match[1]);
  return seconds >= 1 && seconds <= 3600 ? seconds : null;
}

/**
 * Sending a code. Order: no connection, server trouble, rate limit, a
 * specific provider code, an exact provider message (only when there is no
 * code), then the step's generic message. A status alone never picks the
 * address message, and a send error never talks about a code.
 */
export function mapEmailSendError(
  result: AuthCallResult,
  { online = true }: { online?: boolean } = {},
): MappedEmailError {
  if (isConnectionFailure(result, online)) {
    return { key: 'auth.errorOffline', reason: 'offline', retryAfterSeconds: null };
  }
  if (isServerFailure(result)) {
    return { key: 'auth.errorSendServer', reason: 'server', retryAfterSeconds: null };
  }
  if (isRateLimited(result)) {
    const seconds = parseRetryAfterSeconds(result);
    return {
      key: seconds !== null ? 'auth.errorSendRateLimitedWait' : 'auth.errorSendRateLimited',
      reason: 'rate-limited',
      retryAfterSeconds: seconds,
    };
  }
  const message = result.error ?? '';
  const invalidEmail = result.code
    ? result.code === 'email_address_invalid' ||
      (result.code === 'validation_failed' && /email address/i.test(message))
    : /unable to validate email address|^email address ".*" is invalid$/i.test(message);
  if (invalidEmail) {
    return { key: 'auth.errorSendInvalidEmail', reason: 'invalid-email', retryAfterSeconds: null };
  }
  return { key: 'auth.errorSendGeneric', reason: 'generic', retryAfterSeconds: null };
}

/**
 * Checking a code. Same order as sending. The provider reports a wrong code
 * and an expired code identically (`otp_expired`), so they share a message.
 */
export function mapEmailVerifyError(
  result: AuthCallResult,
  { online = true }: { online?: boolean } = {},
): MappedEmailError {
  if (isConnectionFailure(result, online)) {
    return { key: 'auth.errorOffline', reason: 'offline', retryAfterSeconds: null };
  }
  if (isServerFailure(result)) {
    return { key: 'auth.errorVerifyServer', reason: 'server', retryAfterSeconds: null };
  }
  if (isRateLimited(result)) {
    return {
      key: 'auth.errorVerifyRateLimited',
      reason: 'rate-limited',
      retryAfterSeconds: parseRetryAfterSeconds(result),
    };
  }
  const invalidCode = result.code
    ? result.code === 'otp_expired'
    : /token has expired or is invalid/i.test(result.error ?? '');
  if (invalidCode) {
    return { key: 'auth.errorVerifyCode', reason: 'invalid-code', retryAfterSeconds: null };
  }
  return { key: 'auth.errorVerifyGeneric', reason: 'generic', retryAfterSeconds: null };
}

/**
 * Message-only mapping (Google sign-in). Best-effort substring match with a
 * safe generic fallback: Supabase error strings are English and can change
 * across releases, so the generic fallback is the floor and is strictly
 * better than showing the raw message. Returns a key under `auth.*`.
 */
export function mapAuthError(raw: string | null): string {
  const m = (raw ?? '').toLowerCase();
  if (m.includes('expired') || m.includes('invalid') || m.includes('token')) {
    return 'auth.errorInvalidCode';
  }
  if (m.includes('rate') || m.includes('security purposes') || m.includes('seconds')) {
    return 'auth.errorRateLimited';
  }
  return 'auth.errorGeneric';
}
